/**
 * utils/consultantActionItems.ts — FRONTEND MIRROR of supabase/functions/server/consultantActionItemsCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 */


import { courtFor, labelFor, normalizeStatus, type Court, type LifecycleStatus } from './configItemLifecycle.ts';
import { urgencyOf, type Urgency } from './clientHome.ts';
import { readyForBuildErrors } from './configItemReadiness.ts';

/** Why a row is on the list; decides its message and the actions the row offers. */
export type ActionReason =
  | 'review_answers' // the client submitted answers: review them
  | 'build' // locked for build or in build: the firm's move
  | 'blocked' // stopped; unblock when the blocker clears
  | 'overdue_with_client' // an engagement lead's view: the client's item is past due
  | 'client_court' // a client proxy's view: the item waits on the client
  | 'tracking'; // assigned with a due date, waiting on someone else

export interface ActionRow {
  /** `${companyId}:${itemId}`: one row per item, unique across companies. */
  id: string;
  kind: 'task';
  itemId: string;
  companyId: string;
  companyName: string;
  spaceId: string | null;
  /** The space's name; items without a space are company-wide. */
  spaceName: string;
  title: string;
  message: string;
  category: string | null;
  status: LifecycleStatus;
  statusLabel: string;
  blockedFrom: string | null;
  ballInCourt: Court;
  /** On the consultant's list (true) or only tracked (false). */
  needsYou: boolean;
  reason: ActionReason;
  assignedTo: string | null;
  assignedToName: string | null;
  assignmentNote: string | null;
  dueDate: string | null;
  urgency: Urgency;
  daysUntilDue: number | null;
  createdAt: string;
  /** Opens the item on the client page (the drawer, via `?configItem=`). */
  link: string;
  /** What stops a lock for build (only for items still in discovery), for the row's status control. */
  readinessErrors: string[];
  /** Notification-shaped fields older readers of the hook still use. */
  type: 'ready_for_review' | 'ready_for_build' | 'status_change' | 'homework_assigned';
  read: false;
  configItemName: string;
  configItemType: string | null;
}

export interface ActionSpace {
  id: string;
  name?: string;
  status?: string;
}

export interface ActionRowsInput {
  company: { id: string; name?: string };
  items: any[];
  spaces?: ActionSpace[];
  /** The viewer is an engagement lead (or a senior firm role) here: sees blocked and the client's overdue items. */
  engagementLead?: boolean;
  /** The viewer stands in for the client (project lead, stakeholder): sees the client's court. */
  clientProxy?: boolean;
  now?: Date;
}

export const COMPANY_WIDE = 'Company-wide';

/** The link a row opens: the client page with the item's drawer (ClientDetailView reads `configItem`). */
export function actionItemLink(companyId: string, itemId: string): string {
  return `/clients/${encodeURIComponent(companyId)}?configItem=${encodeURIComponent(itemId)}`;
}

const DONE = new Set(['archived', 'deleted', 'removed']);

/**
 * Rows for one company: `needsYou` for what is in the consultant's court (and, for a lead, what is
 * blocked or overdue with the client); `tracking` for what is assigned with a due date and still
 * open but waiting on someone else (the old ConsultantActionItems overview).
 */
export function actionRowsFor(input: ActionRowsInput): { needsYou: ActionRow[]; tracking: ActionRow[] } {
  const now = input.now ?? new Date();
  const spaceNames = new Map<string, string>();
  for (const s of input.spaces ?? []) if (s?.id) spaceNames.set(String(s.id), s.name || 'Unnamed space');
  const needsYou: ActionRow[] = [];
  const tracking: ActionRow[] = [];

  for (const item of input.items ?? []) {
    if (!item?.id || DONE.has(String(item.status ?? ''))) continue;
    const status = normalizeStatus(item);
    if (status === 'approved') continue;
    const court = courtFor(status, item.ballInCourt);
    const { urgency, daysUntilDue } = urgencyOf(item.dueDate ?? null, now);
    // The momentum guard the route had: an item whose work has not started yet is not overdue.
    const notStarted = item.earliestStartDate ? new Date(item.earliestStartDate).getTime() > now.getTime() : false;
    const due = notStarted ? { urgency: 'none' as Urgency, daysUntilDue } : { urgency, daysUntilDue };

    let reason: ActionReason | null = null;
    if (input.clientProxy) {
      if (court === 'client') reason = 'client_court';
    } else if (status === 'blocked') {
      reason = 'blocked';
    } else if (court === 'consultant') {
      reason = status === 'discovery' ? 'review_answers' : 'build';
    } else if (input.engagementLead && court === 'client' && due.urgency === 'overdue') {
      reason = 'overdue_with_client';
    }

    const open = !item.completedAt;
    if (!reason && !(item.assignedTo && item.dueDate && open)) continue;

    const row = rowOf(input.company, item, {
      status,
      court,
      reason: reason ?? 'tracking',
      needsYou: reason !== null,
      urgency: due.urgency,
      daysUntilDue: due.daysUntilDue,
      spaceNames,
    });
    (reason ? needsYou : tracking).push(row);
  }
  return { needsYou, tracking };
}

function rowOf(
  company: { id: string; name?: string },
  item: any,
  facts: { status: LifecycleStatus; court: Court; reason: ActionReason; needsYou: boolean; urgency: Urgency; daysUntilDue: number | null; spaceNames: Map<string, string> },
): ActionRow {
  const itemId = String(item.id);
  const title = String(item.name || item.title || 'Untitled item');
  const spaceId = item.spaceId && !item.isGlobal ? String(item.spaceId) : null;
  return {
    id: `${company.id}:${itemId}`,
    kind: 'task',
    itemId,
    companyId: String(company.id),
    companyName: company.name || 'Unnamed client',
    spaceId,
    spaceName: spaceId ? facts.spaceNames.get(spaceId) ?? 'Unknown space' : COMPANY_WIDE,
    title,
    message: messageFor(facts.reason, facts.status, facts.court, item),
    category: item.category ?? null,
    status: facts.status,
    statusLabel: labelFor(facts.status),
    blockedFrom: item.blockedFrom ?? null,
    ballInCourt: facts.court,
    needsYou: facts.needsYou,
    reason: facts.reason,
    assignedTo: item.assignedTo ?? null,
    assignedToName: item.assignedToName ?? null,
    assignmentNote: item.assignmentNote ?? null,
    dueDate: item.dueDate ?? null,
    urgency: facts.urgency,
    daysUntilDue: facts.daysUntilDue,
    createdAt: String(item.submittedForReviewAt || item.lastModified || item.updatedAt || item.createdAt || ''),
    link: actionItemLink(String(company.id), itemId),
    readinessErrors: facts.status === 'discovery' ? readyForBuildErrors({ ...item, companyId: company.id }) : [],
    type: facts.reason === 'blocked' ? 'status_change' : facts.reason === 'review_answers' ? 'ready_for_review' : facts.reason === 'build' ? 'ready_for_build' : 'homework_assigned',
    read: false,
    configItemName: title,
    configItemType: item.type ?? null,
  };
}

function messageFor(reason: ActionReason, status: LifecycleStatus, court: Court, item: any): string {
  switch (reason) {
    case 'blocked':
      return `Blocked: ${item.blockerReason || item.blockedReason || 'no reason given'}`;
    case 'review_answers':
      return 'The client submitted answers for your review';
    case 'build':
      return status === 'ready_for_build' ? 'Locked for build; start when ready' : 'In build; hand to the client for review when built';
    case 'overdue_with_client':
      return `Past due with the client (${labelFor(status).toLowerCase()})`;
    case 'client_court':
      return `Waiting on the client (${labelFor(status).toLowerCase()})`;
    default:
      return court === 'client' ? 'Waiting on the client' : `Waiting on ${item.assignedToName || 'the assignee'}`;
  }
}

const URGENCY_RANK: Record<Urgency, number> = { overdue: 0, today: 1, tomorrow: 2, thisWeek: 3, none: 4 };

/** Most urgent first: by due urgency, blocked before the rest, the earliest due date, then the title. */
export function compareActionRows(a: ActionRow, b: ActionRow): number {
  return (
    URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] ||
    Number(b.reason === 'blocked') - Number(a.reason === 'blocked') ||
    String(a.dueDate ?? '9999').localeCompare(String(b.dueDate ?? '9999')) ||
    a.title.localeCompare(b.title)
  );
}

export interface ActionCounts {
  total: number;
  overdue: number;
  dueToday: number;
  dueTomorrow: number;
}

/** The four counts the old overview's cards showed: total, overdue, due today, due tomorrow. */
export function actionCounts(rows: Array<Pick<ActionRow, 'urgency'>>): ActionCounts {
  const counts: ActionCounts = { total: rows.length, overdue: 0, dueToday: 0, dueTomorrow: 0 };
  for (const r of rows) {
    if (r.urgency === 'overdue') counts.overdue++;
    else if (r.urgency === 'today') counts.dueToday++;
    else if (r.urgency === 'tomorrow') counts.dueTomorrow++;
  }
  return counts;
}

export interface ActionBreakdown {
  assigned_to_me: number;
  waiting_review: number;
  blocked: number;
  overdue: number;
  due_today: number;
  due_tomorrow: number;
  /** Due within three days and not overdue. */
  due_soon: number;
}

/** The chips above the list. "Assigned to me" is the viewer's own assignments. */
export function actionBreakdown(rows: ActionRow[], viewerId: string): ActionBreakdown {
  const c = actionCounts(rows);
  return {
    assigned_to_me: rows.filter((r) => r.assignedTo && r.assignedTo === viewerId).length,
    waiting_review: rows.filter((r) => r.reason === 'review_answers').length,
    blocked: rows.filter((r) => r.reason === 'blocked').length,
    overdue: c.overdue,
    due_today: c.dueToday,
    due_tomorrow: c.dueTomorrow,
    due_soon: rows.filter((r) => r.daysUntilDue !== null && r.daysUntilDue >= 0 && r.daysUntilDue <= 3 && r.urgency !== 'none').length,
  };
}

export interface ActionSpaceGroup {
  spaceId: string | null;
  spaceName: string;
  rows: ActionRow[];
}

export interface ActionCompanyGroup {
  companyId: string;
  companyName: string;
  counts: ActionCounts;
  spaces: ActionSpaceGroup[];
}

/**
 * The list's shape: one group per company (most overdue first, then by name), inside it one per
 * space (company-wide first, then by name), rows most urgent first.
 */
export function groupActionRows(rows: ActionRow[]): ActionCompanyGroup[] {
  const companies = new Map<string, { companyName: string; spaces: Map<string, ActionSpaceGroup>; rows: ActionRow[] }>();
  for (const row of rows) {
    let company = companies.get(row.companyId);
    if (!company) {
      company = { companyName: row.companyName, spaces: new Map(), rows: [] };
      companies.set(row.companyId, company);
    }
    company.rows.push(row);
    const key = row.spaceId ?? '';
    let space = company.spaces.get(key);
    if (!space) {
      space = { spaceId: row.spaceId, spaceName: row.spaceName, rows: [] };
      company.spaces.set(key, space);
    }
    space.rows.push(row);
  }
  const groups: ActionCompanyGroup[] = [...companies.entries()].map(([companyId, c]) => ({
    companyId,
    companyName: c.companyName,
    counts: actionCounts(c.rows),
    spaces: [...c.spaces.values()]
      .map((s) => ({ ...s, rows: [...s.rows].sort(compareActionRows) }))
      .sort((a, b) => Number(b.spaceId === null) - Number(a.spaceId === null) || a.spaceName.localeCompare(b.spaceName)),
  }));
  return groups.sort((a, b) => b.counts.overdue - a.counts.overdue || a.companyName.localeCompare(b.companyName));
}
